import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Users,
  Loader2,
  ArrowRight,
  CheckCircle2,
  Merge,
  AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const TERMINAL = new Set(['Declined', 'Deal Lost', 'Ghosted']);

export default function ConsolidateOutreachPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyGroup, setBusyGroup] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/outreach/merge-candidates`, { withCredentials: true });
      setGroups(r.data?.groups || []);
    } catch {
      toast.error('Failed to load merge candidates');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const mergeGroup = async (g) => {
    if (!window.confirm(`Consolidate ${g.count} outreach rows for ${g.pharma_name} × ${g.asset_name || 'no asset'}?\n\nAll secondary contacts will be absorbed into the most-advanced row. The chosen primary's status will follow the "terminal wins / most-advanced wins" rule.`)) return;
    setBusyGroup(`${g.biotech_company_id}|${g.pharma_name}|${g.asset_name}`);
    try {
      // Choose primary: prefer terminal-status row, else most-recent row.
      const primary = pickPrimary(g.rows);
      const others = g.rows.filter(r => r.id !== primary.id);
      let merged = 0, failed = 0;
      for (const o of others) {
        try {
          await axios.post(`${API}/outreach/merge`, { from_id: o.id, into_id: primary.id }, { withCredentials: true });
          merged += 1;
        } catch (e) {
          failed += 1;
          console.error('[consolidate] merge failed', o.id, e?.response?.data || e);
        }
      }
      toast.success(`Merged ${merged} contacts into primary${failed ? ` (${failed} failed)` : ''}`);
      load();
    } finally { setBusyGroup(null); }
  };

  const STAGE_ORDER = [
    'Introduction Made', 'Already in Touch', 'Asset Under Evaluation',
    'Meeting Requested', 'Meeting Scheduled', 'CDA in Progress', 'CDA Signed', 'Data Shared / Data Room Access',
    'Awaiting Trial Data', 'Due Diligence', 'Deal Discussion',
    'Term Sheet Shared', 'Deal Closed', 'On Hold',
  ];
  function pickPrimary(rows) {
    // Terminal-status row wins (so we keep all the decline notes etc.)
    const terminal = rows.find(r => TERMINAL.has(r.status));
    if (terminal) return terminal;
    // Most-advanced canonical stage wins
    const ranked = [...rows].sort((a, b) => {
      const ra = STAGE_ORDER.indexOf(a.status); const rb = STAGE_ORDER.indexOf(b.status);
      return rb - ra;
    });
    return ranked[0];
  }

  return (
    <div className="max-w-6xl mx-auto p-8">
      <Link to="/outreach" className="text-xs text-slate-500 hover:underline">← Back to Outreach</Link>
      <div className="flex items-center gap-3 mt-2 mb-1">
        <Users className="w-6 h-6 text-emerald-600" />
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Consolidate Outreach Duplicates
        </h1>
      </div>
      <p className="text-xs text-slate-500 max-w-2xl mb-6">
        These outreach rows share the same biotech + pharma + asset and should
        live as a single conversation with multiple contacts. Click <b>Consolidate</b>
        to merge each group — the most-advanced (or terminal) status wins, all
        contact info is preserved.
      </p>

      {loading ? (
        <div className="text-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
          <div className="text-sm text-slate-600">No duplicates found. Your outreach list is already consolidated. 🎉</div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            <span className="font-semibold text-slate-900">{groups.length}</span> group{groups.length === 1 ? '' : 's'} found.
            Total absorbable rows: <span className="font-semibold text-slate-900">{groups.reduce((s, g) => s + (g.count - 1), 0)}</span>.
          </div>
          {groups.map((g) => {
            const groupKey = `${g.biotech_company_id}|${g.pharma_name}|${g.asset_name}`;
            const isBusy = busyGroup === groupKey;
            const primary = pickPrimary(g.rows);
            return (
              <div key={groupKey} className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold text-slate-900">{g.pharma_name || '(unknown pharma)'}</div>
                      <span className="text-slate-300">·</span>
                      <div className="text-xs text-slate-600">{g.biotech_company_name || '(unknown biotech)'}</div>
                      <span className="text-slate-300">·</span>
                      <Badge variant="outline" className="text-[10px]">{g.asset_name || 'no asset'}</Badge>
                      <Badge className="bg-amber-100 text-amber-800 text-[10px]">{g.count} rows</Badge>
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1.5">
                      {g.rows.map(r => (
                        <div key={r.id} className={`text-[11px] px-2 py-1 rounded border ${r.id === primary.id ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-slate-50'}`}>
                          <div className="flex items-center gap-1">
                            {r.id === primary.id && <span className="text-[9px] uppercase font-semibold text-emerald-700">primary</span>}
                            <span className="font-medium text-slate-800">{r.contact_name || '(no name)'}</span>
                            {r.designation && <span className="text-slate-400">· {r.designation}</span>}
                          </div>
                          <div className="text-slate-500 flex items-center justify-between gap-2 mt-0.5">
                            <span className="truncate">{r.contact_email || ''}</span>
                            {r.status && <Badge variant="outline" className={`text-[9px] ${TERMINAL.has(r.status) ? 'border-rose-200 text-rose-700' : ''}`}>{r.status}</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button
                    onClick={() => mergeGroup(g)}
                    disabled={isBusy}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
                    data-testid={`consolidate-${groupKey}`}
                  >
                    {isBusy ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Merging…</>
                    ) : (
                      <><Merge className="w-3 h-3 mr-1" /> Consolidate</>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
